/**
 * Optimaxx Plus — Interactive Timeline Diagram
 * Recreates the savings plan flow visually with hoverable/clickable milestones.
 * Keeps the Allianz brand since this is the product being promoted.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Award, TrendingUp, Coins, Percent, Calendar,
  Sparkles, ShieldCheck, Globe2, Crown, BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { analytics } from '../../services/analytics';
import './OptimaxxTimeline.css';

const MILESTONES = [
  {
    id: 'mes1',
    label: 'Mes 1',
    title: 'Arrancas tu plan',
    icon: Sparkles,
    color: '#FF4757',
    detail: 'Eliges tu portafolio de inversión (Peso, Dólar, Euro, Oro, índices globales) y empieza tu ahorro automático. Prima única inicial de $500 (seguro por fallecimiento).'
  },
  {
    id: 'periodo-inicial',
    label: 'Período Inicial',
    title: 'Construyes disciplina y ganas tu bono',
    icon: Award,
    color: '#FFB800',
    detail: 'Durante los primeros 18 meses se establece el hábito. Aquí ya estás generando rendimiento y acumulando. ¡Premio por tu constancia! En el mes 12 ya se refleja tu bono de fidelidad (del 55% al 100% sobre tu aportación anual). El porcentaje depende de tu aportación mensual y el plazo elegido (única ocasión).'
  },
  {
    id: 'mes19',
    label: 'Mes 19+',
    title: 'Flexibilidad total',
    icon: TrendingUp,
    color: '#39FF14',
    detail: 'A partir del mes 19 puedes disponer de tu dinero (lo que tengas disponible del mes 19 en adelante, mínimo $10,000 con 1% de comisión), suspender aportaciones, hacer traspasos entre portafolios sin costo, y aumentar o disminuir tus aportaciones.'
  },
  {
    id: 'anos25',
    label: '25 años',
    title: 'Liquidación máxima',
    icon: Coins,
    color: '#A855F7',
    detail: 'Tu plan llega a madurez total. El interés compuesto en este punto multiplica significativamente tus aportaciones iniciales.'
  }
];

const PORTFOLIO_ITEMS = [
  { label: 'Peso', group: 'currency' },
  { label: 'Dólar', group: 'currency' },
  { label: 'Euro', group: 'currency' },
  { label: 'Oro', group: 'commodity' },
  { label: 'UDIS', group: 'index' },
  { label: 'BRIC', group: 'region' },
  { label: 'China', group: 'region' },
  { label: 'Brasil', group: 'region' },
  { label: 'M. Emergentes', group: 'region' },
  { label: 'Clean Energy', group: 'theme' },
  { label: 'ACWI', group: 'index' },
  { label: 'NASDAQ', group: 'index' }
];

const FISCAL_ITEMS = [
  { tag: 'Art. 151', label: 'Deducible de ISR', highlight: true },
  { tag: 'Art. 93', label: 'Libre de impuestos al retiro' },
  { tag: 'Fideicomiso', label: 'De ahorro de largo plazo' },
  { tag: 'Retiro', label: 'Desde 60 o 65 años' }
];

const BONUS_TIERS = [
  { min: 2000, max: 2999, bonus: 55, label: '$2,000/mes' },
  { min: 3000, max: 4999, bonus: 65, label: '$3,000 – $4,000' },
  { min: 5000, max: 7999, bonus: 75, label: '$5,000' },
  { min: 8000, max: 10000, bonus: 100, label: '$8,000+', top: true }
];

const getBonusForAmount = (amount) => {
  if (amount < 2000) return { bonus: 0, tier: null, nextTier: BONUS_TIERS[0] };
  const tier = [...BONUS_TIERS].reverse().find((t) => amount >= t.min) || BONUS_TIERS[0];
  const nextTier = BONUS_TIERS.find((t) => t.min > amount) || null;
  return { bonus: tier.bonus, tier, nextTier };
};

// 🎉 Confetti burst — pure DOM, no extra deps. Attaches to the given host element
// (or document.body as fallback). Auto-cleans after 2.5s.
const CONFETTI_COLORS = ['#39FF14', '#FFB800', '#FF4757', '#4FACFE', '#A855F7', '#FFD700'];

const fireBonusConfetti = (host) => {
  const target = host || document.body;
  const container = document.createElement('div');
  container.className = 'bonus-confetti-container';
  target.appendChild(container);

  const pieces = 36;
  for (let i = 0; i < pieces; i += 1) {
    const piece = document.createElement('span');
    piece.className = 'bonus-confetti-piece';
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.backgroundColor = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
    piece.style.animationDelay = `${Math.random() * 0.25}s`;
    piece.style.animationDuration = `${1.4 + Math.random() * 0.7}s`;
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    piece.style.width = `${6 + Math.random() * 6}px`;
    piece.style.height = `${10 + Math.random() * 8}px`;
    container.appendChild(piece);
  }

  setTimeout(() => container.remove(), 2500);
};

const OptimaxxTimeline = () => {
  const [activeMilestone, setActiveMilestone] = useState('mes1');
  const [bonusAmount, setBonusAmount] = useState(3000);
  const active = MILESTONES.find((m) => m.id === activeMilestone);
  const maxReachedRef = useRef(false);
  const bonusCardRef = useRef(null);

  const bonusInfo = useMemo(() => {
    const { bonus, tier, nextTier } = getBonusForAmount(bonusAmount);
    const annualContribution = bonusAmount * 12;
    const bonusAmountMxn = (annualContribution * bonus) / 100;
    return { bonus, tier, nextTier, annualContribution, bonusAmountMxn };
  }, [bonusAmount]);

  // 🎉 Confetti + toast when user crosses into the 100% tier
  useEffect(() => {
    if (bonusAmount >= 8000 && !maxReachedRef.current) {
      maxReachedRef.current = true;
      fireBonusConfetti(bonusCardRef.current);
      toast('🎉 ¡Tier máximo desbloqueado!', {
        description: 'Bono al 100% sobre tu aportación anual. Ese es el premio gordo de la fidelidad 👑',
        duration: 6000,
        className: 'toast-bonus-max'
      });
      analytics.ctaClick('bonus_max_tier_reached');
    } else if (bonusAmount < 8000 && maxReachedRef.current) {
      // Reset so the user can re-trigger if they slide back up
      maxReachedRef.current = false;
    }
  }, [bonusAmount]);

  return (
    <div className="optimaxx-timeline" data-testid="optimaxx-timeline">
      <div className="optimaxx-timeline-header">
        {/* Inline brand badge — no external image dependency */}
        <div className="optimaxx-allianz-badge" data-testid="optimaxx-allianz-badge">
          <ShieldCheck className="optimaxx-allianz-shield" />
          <div className="optimaxx-allianz-text">
            <span className="optimaxx-allianz-label">Producto por</span>
            <span className="optimaxx-allianz-brand">Allianz México</span>
          </div>
        </div>
        <div>
          <h3 className="optimaxx-timeline-title">¿Cómo funciona tu plan Optimaxx Plus?</h3>
          <p className="optimaxx-timeline-subtitle">
            Haz clic en cada etapa para ver qué pasa con tu dinero a lo largo del tiempo 🕒
          </p>
        </div>
      </div>

      {/* Interactive Timeline */}
      <div className="timeline-track-wrapper">
        <div className="timeline-track" aria-hidden="true" />
        <div
          className="timeline-progress"
          style={{ width: `${(MILESTONES.findIndex((m) => m.id === activeMilestone) / (MILESTONES.length - 1)) * 100}%` }}
        />
        <div className="timeline-milestones">
          {MILESTONES.map((m) => {
            const Icon = m.icon;
            const isActive = m.id === activeMilestone;
            return (
              <button
                key={m.id}
                type="button"
                className={`timeline-milestone ${isActive ? 'milestone-active' : ''}`}
                onClick={() => setActiveMilestone(m.id)}
                style={{ '--milestone-color': m.color }}
                data-testid={`milestone-${m.id}`}
                aria-label={`Etapa: ${m.title}`}
              >
                <div className="milestone-dot">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="milestone-label">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Milestone Detail */}
      <div className="milestone-detail" style={{ '--detail-color': active.color }}>
        <div className="milestone-detail-header">
          <active.icon className="milestone-detail-icon" />
          <div>
            <span className="milestone-detail-badge">{active.label}</span>
            <h4 className="milestone-detail-title">{active.title}</h4>
          </div>
        </div>
        <p className="milestone-detail-text">{active.detail}</p>
      </div>

      {/* Features — 3 visually distinct cards */}
      <div className="optimaxx-features-grid">
        {/* Card 1: Portfolios as chips */}
        <div className="optimaxx-feature-card feature-card-portfolios" data-testid="feature-portfolios">
          <div className="optimaxx-feature-card-header">
            <div className="optimaxx-feature-icon-wrap" style={{ '--accent': '#FFB800' }}>
              <Globe2 className="optimaxx-feature-icon" />
            </div>
            <div>
              <h5 className="optimaxx-feature-title">Portafolios de Inversión</h5>
              <span className="optimaxx-feature-subtitle">12 opciones globales</span>
            </div>
          </div>
          <div className="portfolio-chips">
            {PORTFOLIO_ITEMS.map((p) => (
              <span key={p.label} className={`portfolio-chip chip-${p.group}`}>
                {p.label}
              </span>
            ))}
          </div>
        </div>

        {/* Card 2: Fiscal badges */}
        <div className="optimaxx-feature-card feature-card-fiscal" data-testid="feature-fiscal">
          <div className="optimaxx-feature-card-header">
            <div className="optimaxx-feature-icon-wrap" style={{ '--accent': '#4FACFE' }}>
              <Percent className="optimaxx-feature-icon" />
            </div>
            <div>
              <h5 className="optimaxx-feature-title">Beneficios Fiscales</h5>
              <span className="optimaxx-feature-subtitle">Optimización legal (Ley ISR)</span>
            </div>
          </div>
          <ul className="fiscal-list">
            {FISCAL_ITEMS.map((f) => (
              <li key={f.tag} className={`fiscal-item ${f.highlight ? 'fiscal-item-highlight' : ''}`}>
                <span className="fiscal-tag">{f.tag}</span>
                <span className="fiscal-label">
                  {f.label}
                  {f.highlight && <BadgeCheck className="fiscal-check" />}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Card 3: Bonus tiers — INTERACTIVE SLIDER */}
        <div ref={bonusCardRef} className="optimaxx-feature-card feature-card-bonus" data-testid="feature-bonus">
          <div className="optimaxx-feature-card-header">
            <div className="optimaxx-feature-icon-wrap" style={{ '--accent': '#39FF14' }}>
              <Award className="optimaxx-feature-icon" />
            </div>
            <div>
              <h5 className="optimaxx-feature-title">Bono de Fidelidad</h5>
              <span className="optimaxx-feature-subtitle">Se refleja en el mes 12 — depende de tu aporte y plazo</span>
            </div>
          </div>

          {/* Slider */}
          <div className="bonus-slider-wrapper">
            <div className="bonus-slider-row">
              <label htmlFor="bonus-slider" className="bonus-slider-label">Aporte mensual</label>
              <span className="bonus-slider-amount">
                ${bonusAmount.toLocaleString('es-MX')}
                {bonusAmount >= 8000 && <Crown className="bonus-crown bonus-crown-inline" />}
              </span>
            </div>
            <input
              id="bonus-slider"
              type="range"
              min="1000"
              max="10000"
              step="500"
              value={bonusAmount}
              onChange={(e) => setBonusAmount(parseInt(e.target.value, 10))}
              className="bonus-slider"
              style={{ '--fill': `${((bonusAmount - 1000) / 9000) * 100}%` }}
              data-testid="bonus-slider"
              aria-label="Aporte mensual para calcular bono"
            />
            <div className="bonus-slider-hints">
              <span>$1,000</span>
              <span>$10,000</span>
            </div>
          </div>

          {/* Big % result */}
          <div className={`bonus-result ${bonusInfo.bonus === 100 ? 'bonus-result-top' : ''}`} data-testid="bonus-result">
            {bonusInfo.bonus > 0 ? (
              <>
                <div className="bonus-result-percent">
                  +<span className="bonus-result-number">{bonusInfo.bonus}</span>%
                </div>
                <p className="bonus-result-detail">
                  En el mes 12 recibirías <strong>${bonusInfo.bonusAmountMxn.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</strong> de bono
                  <span className="bonus-result-sub">(sobre tu aportación anual de ${bonusInfo.annualContribution.toLocaleString('es-MX')})</span>
                </p>
                {bonusInfo.nextTier && (
                  <p className="bonus-result-hint">
                    💡 Sube a <strong>${bonusInfo.nextTier.min.toLocaleString('es-MX')}/mes</strong> y tu bono salta a <strong className="text-neon">+{bonusInfo.nextTier.bonus}%</strong>
                  </p>
                )}
                {!bonusInfo.nextTier && (
                  <p className="bonus-result-hint">
                    👑 Estás en el tier máximo. Bono al 100% — no se puede pedir más.
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="bonus-result-percent bonus-result-locked">+0%</div>
                <p className="bonus-result-detail">
                  Necesitas <strong>$2,000/mes</strong> mínimo para desbloquear el bono.
                  <span className="bonus-result-sub">Sigue subiendo el slider 👆</span>
                </p>
              </>
            )}
          </div>

          {/* Tier ladder visualization */}
          <div className="bonus-tier-ladder" aria-hidden="true">
            {BONUS_TIERS.map((t) => {
              const reached = bonusAmount >= t.min;
              const isCurrent = bonusInfo.tier && bonusInfo.tier.min === t.min;
              return (
                <div
                  key={t.min}
                  className={`bonus-tier-pill ${reached ? 'tier-reached' : ''} ${isCurrent ? 'tier-current' : ''}`}
                >
                  <span className="tier-pill-amount">{t.label}</span>
                  <span className="tier-pill-bonus">+{t.bonus}%</span>
                </div>
              );
            })}
          </div>
          <Calendar className="bonus-calendar-watermark" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

export default OptimaxxTimeline;
